import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import GimManagerInstallButton from './HeaderComponents/GimManagerInstallButton.jsx';
import {
  faChevronDown,
  faDownload,
  faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Image,
  Nav,
  NavDropdown,
  Navbar,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import Headroom from '@integreat-app/react-sticky-headroom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';

import { TranslationContext } from '../../../App.jsx';
import logo from '../../../assets/logo.png';
import gimLogoWhite from '../../../assets/transparent-logo-white.png';
import gimLogoYellow from '../../../assets/transparent-logo-yellow.png';

import {
  changeLanuage,
  setHideInstallButton,
  userSignOut,
} from '../../../redux/actions/user/user.actions.js';
import {
  userAvatarSelector,
  userIdSelector,
  userInfoSelector,
} from '../../../redux/selectors/user.selector.js';
import { routePaths } from '../../../shared/config/globalConfig.jsx';
import {
  downloadFromUrl,
  logDebug,
} from '../../../shared/config/globalFunctions.js';
import InstallGiManagerModal from '../../gim-components/InstallGimManagerModal/index.jsx';
import Signin from './Signin.jsx';
import './Header.scss';

// --------- NEW IMPORTS: Redux Selectors for GIMmanager ---------
import {
  GIMmanagerUpdateSelector,
  LatestGIMmanagerversionSelector,
} from '../../../redux/selectors/desktopApp.selector.js';

import {
  languageOptions,
  AdminLanguageOptions,
  adminRoutes,
  mainRoutes,
} from '../../../shared/config/headerConfigs.jsx';

import { ShowInstallerButtonSelector } from '../../../redux/selectors/desktopApp.selector.js';

/**
 * @returns {import('react').ReactElement}
 */
const Header = (props) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useContext(TranslationContext);
  const userInfo = useSelector(userInfoSelector);
  const userAvatar = useSelector(userAvatarSelector);

  const { isAdmin } = useSelector((/** @type {any} */ state) => state?.auth);
  const { hideInstallButton, isMobileView } = useSelector(
    (/** @type {any} */ state) => state?.user
  );
  logDebug('hide install button?', hideInstallButton);
  //logger('is mobile?', isMobileView);
  const userid = useSelector(userIdSelector);
  const [modalShow, setModalShow] = useState(false);
  const [hoverState, setHoverState] = useState('');

  // --- Redux state for GIMManagerInstallButton ---
  const showInstallerButton = useSelector(ShowInstallerButtonSelector);
  const appUpdateAvailable = useSelector(GIMmanagerUpdateSelector);
  const latestAppVersion = useSelector(LatestGIMmanagerversionSelector);

  // ---------- Helpers ----------

  const clearCacheData = () => {
    caches.keys().then((names) => {
      names.forEach((name) => {
        caches.delete(name);
      });
    });
  };

  const handleSignout = () => {
    // If you want showAppLoader in Redux, add it there too.
    dispatch(userSignOut());
    clearCacheData();
    setTimeout(() => {
      // e.g. dispatch(setShowAppLoader(false))
      // window.location.reload()
    }, 500);
  };

  const changeLanguageHandler = useCallback(
    (e) => {
      const languageValue = e?.value || e?.target?.value;
      i18n.changeLanguage(languageValue);
      dispatch(changeLanuage(languageValue));
    },
    [dispatch, i18n]
  );

  useEffect(() => {
    if (adminRoutes.includes(location.pathname)) {
      if (i18n.language === 'en' || i18n.language === 'nl') {
        logDebug(i18n.language);
      } else {
        changeLanguageHandler(languageOptions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const languageMenuOptions = useMemo(
    () =>
      adminRoutes.includes(location.pathname)
        ? AdminLanguageOptions
        : languageOptions,
    [location.pathname]
  );

  const renderLanguages = useCallback(() => {
    return languageMenuOptions.map((ele, i) => (
      <div
        key={i}
        className={`language-section ${i18n.language === ele?.value ? 'active-language' : ''}`}
        onClick={() => {
          changeLanguageHandler(ele);
          document.body.click();
        }}
      >
        {ele?.label}
      </div>
    ));
  }, [languageMenuOptions, changeLanguageHandler, i18n?.language]);

  useEffect(() => {
    document.body.click();
  }, [hoverState]);

  const UserMenu = () => {
    return (
      <div
        className="user-profile-head"
        style={{ margin: userAvatar && '0px 35px' }}
        onMouseEnter={() => onHoverView('MyProject')}
        onMouseLeave={() => onHoverView('')}
        onClick={() =>
          !isMobileView &&
          navigate({
            pathname: `${routePaths.projects}`,
          })
        }
      >
        {userAvatar ? (
          <Image src={userAvatar} alt="" height={30} width={30} roundedCircle />
        ) : (
          <span
            className="dot"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {`${userInfo?.FirstName?.charAt(0) ?? userInfo?.localizedFirstName?.charAt(0) ?? ''}${userInfo?.SurName?.charAt(0) ?? userInfo?.localizedLastName?.charAt(0) ?? ''}`}
          </span>
        )}
      </div>
    );
  };

  const RemoveElement = useCallback(
    (type) => {
      if (!isMobileView) {
        setTimeout(() => {
          const element = document.querySelector('.dropdown-menu.show');
          if (element && window.innerWidth <= 992) {
            element.classList.remove('show');
          }
        }, 100);
      }
    },
    [isMobileView]
  );

  const onHoverView = useCallback(
    (type) => {
      if (!isMobileView) {
        setHoverState(type);
      }
    },
    [isMobileView]
  );

  return (
    <Headroom
      scrollHeight={100}
      height={50}
      className={`hide-nav head-wrapper ${
        (location.pathname?.includes(routePaths.proInfo) ||
          location.pathname?.includes(routePaths.transferApp) ||
          location.pathname?.includes(routePaths.storeInfo) ||
          location.pathname?.includes(routePaths.mapsInfo)) &&
        !location?.pathname?.includes(routePaths?.projects)
          ? ''
          : 'app-nav'
      }`}
    >
      <Navbar className="header-nav" expand="lg" collapseOnSelect={true}>
        <Navbar.Brand
          className={
            (location.pathname.includes(routePaths.proInfo) ||
              location.pathname.includes(routePaths.storeInfo)) &&
            location.pathname !== routePaths?.projects
              ? 'pro-brand'
              : 'brand'
          }
          data-testid="rootLink"
          href="https://gimtransfer.com"
        >
          <img src={logo} className="d-inline-block align-top" alt="Logo" />
        </Navbar.Brand>

        {location.pathname !== routePaths.gim ? (
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="mr-auto pro-nav">
              <Nav.Link
                id="GimTransfer"
                data-testid="storeButton"
                eventKey="4"
                as={Link}
                to={`${routePaths.transferApp}`}
                className="items logo-items"
                active={
                  location.pathname.includes(routePaths.transferApp) ||
                  hoverState === 'app'
                }
              >
                <img
                  className="gim-logo"
                  style={{ width: '40px', marginBottom: '4px' }}
                  src={
                    location.pathname.includes(routePaths.transferApp) ||
                    hoverState === 'app'
                      ? gimLogoYellow
                      : gimLogoWhite
                  }
                  alt=""
                />
                transfer
              </Nav.Link>
              <Nav.Link
                id="GimStore"
                data-testid="storeButton"
                eventKey="3"
                as={Link}
                to={`${routePaths.store}`}
                className="items logo-items"
                active={
                  location.pathname.includes(routePaths.store) ||
                  hoverState === 'store'
                }
              >
                <img
                  className="gim-logo"
                  style={{ width: '40px', marginBottom: '4px' }}
                  src={
                    location.pathname.includes(routePaths.store) ||
                    hoverState === 'store'
                      ? gimLogoYellow
                      : gimLogoWhite
                  }
                  alt=""
                />
                store
              </Nav.Link>

              <Fragment>
                <Nav.Link
                  id="GimPro"
                  data-testid="proLink"
                  eventKey="2"
                  as={Link}
                  to={`${routePaths.pro}`}
                  className="items"
                  active={
                    (location.pathname.includes(routePaths.pro) &&
                      location.pathname !== routePaths?.projects) ||
                    hoverState === 'pro'
                  }
                >
                  <img
                    className="gim-logo"
                    style={{ width: '40px', marginBottom: '4px' }}
                    src={
                      (location.pathname.includes(routePaths.pro) &&
                        location.pathname !== routePaths?.projects) ||
                      hoverState === 'pro'
                        ? gimLogoYellow
                        : gimLogoWhite
                    }
                    alt=""
                  />
                  pro
                </Nav.Link>

                <Nav.Link
                  id="GimMaps"
                  data-testid="mapsLink"
                  eventKey="3"
                  as={Link}
                  to={`${routePaths.maps}`}
                  className="items"
                  active={location.pathname.includes(routePaths.maps)}
                >
                  <img
                    className="gim-logo"
                    style={{ width: '40px', marginBottom: '4px' }}
                    src={
                      location.pathname.includes(routePaths.maps)
                        ? gimLogoYellow
                        : gimLogoWhite
                    }
                    alt=""
                  />
                  maps
                </Nav.Link>
              </Fragment>
            </Nav>

            {[`${routePaths.pro}`, `${routePaths.store}`].includes(
              location.pathname
            ) && !Object.keys(userInfo).length ? (
              <div className="sign-in-text">
                {t('Header.pleaseSignInToSeeMoreProjects')}
              </div>
            ) : null}
          </Navbar.Collapse>
        ) : null}

        <div
          className="justify-content-end d-flex headRightBar"
          data-testid="NavCollapse"
        >
          <>
            {/* ----------- GIMmanager Install/Update Button (Redux Only) ----------- */}
            <GimManagerInstallButton
              showInstallerButton={showInstallerButton}
              appUpdateAvailable={appUpdateAvailable}
              latestAppVersion={latestAppVersion}
              t={t}
              show={
                !hideInstallButton && mainRoutes.includes(location.pathname)
              }
              onInstallClick={() => setModalShow(true)}
              onUpdateClick={() => {
                let url = latestAppVersion
                  ? `${import.meta.env.VITE_DOMAIN}GIMmanager ${latestAppVersion}.exe`
                  : `${import.meta.env.VITE_DOMAIN}GIMmanager.exe`;
                let name = latestAppVersion
                  ? `GIMmanager ${latestAppVersion}.exe`
                  : 'GIMmanager.exe';

                downloadFromUrl(url, name);
                dispatch(setHideInstallButton(true));
                setTimeout(
                  () => {
                    dispatch(setHideInstallButton(false));
                  },
                  6 * 60 * 1000
                );
              }}
            />

            {/* Modal - you can refactor to use Redux if needed */}
            <InstallGiManagerModal
              show={modalShow}
              hide={() => setModalShow(false)}
              // onHide={() => setModalShow(false)}
              // appContext={appContext}    // REMOVE THIS LINE
              // setAppContext={setAppContext} // REMOVE THIS LINE
              t={t}
            />

            <OverlayTrigger
              trigger="click"
              rootClose
              key="bottom-language"
              placement="bottom"
              overlay={
                <Popover
                  id="popover-positioned-bottom"
                  className="lang-menu"
                  style={{ marginTop: 10 }}
                >
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '70px 70px',
                    }}
                  >
                    {renderLanguages()}
                  </div>
                </Popover>
              }
            >
              <Nav.Link
                id="language"
                data-testid="languageButton"
                className="items logo-items globe-button"
              >
                <FontAwesomeIcon
                  data-testid="globeButton"
                  id="globe"
                  icon={faGlobe}
                  role="button"
                  style={{
                    fontSize: 25,
                    color: 'white',
                  }}
                />
              </Nav.Link>
            </OverlayTrigger>
          </>
          {userInfo && Object.keys(userInfo).length > 0 && userid ? (
            <div className="userInfo">
              <NavDropdown
                renderMenuOnMount={true}
                id="basic-nav-dropdown"
                className={`text-center user-profile ${
                  location.pathname === routePaths?.projects ||
                  hoverState === 'MyProject'
                    ? 'user-active-link show'
                    : ''
                }`}
                data-testid="navDropdown"
                title={UserMenu()}
                onClick={(e) => logDebug('click event', e)}
                onMouseEnter={(e) => {
                  onHoverView('MyProject');
                }}
                onMouseLeave={() => onHoverView('')}
                show={hoverState !== ''}
              >
                <NavDropdown.Item
                  id="my-projects"
                  className={
                    location.pathname === routePaths?.projects ||
                    hoverState === 'MyProject'
                      ? 'user-active-link'
                      : ' '
                  }
                  onMouseEnter={() => onHoverView('MyProject')}
                  //onMouseLeave={() => onHoverView('')}
                  as={Link}
                  to={`${routePaths.projects}`}
                >
                  {t('Global.myProjects')}
                </NavDropdown.Item>

                {isAdmin && (
                  <NavDropdown.Item
                    className={
                      location.pathname === routePaths?.invoiceList ||
                      hoverState === 'Invoicelist'
                        ? 'user-active-link'
                        : ''
                    }
                    as={Link}
                    to={`${routePaths.invoiceList}`}
                    onMouseEnter={() => onHoverView('Invoicelist')}
                    //onMouseLeave={() => onHoverView('')}
                  >
                    {t('Header.invoiceList')}
                  </NavDropdown.Item>
                )}

                {isAdmin && (
                  <NavDropdown.Item
                    className={
                      location.pathname === routePaths?.projectOverview ||
                      hoverState === 'ProjectOverview'
                        ? 'user-active-link'
                        : ' '
                    }
                    onMouseEnter={() => onHoverView('ProjectOverview')}
                    onMouseLeave={() => onHoverView('')}
                    as={Link}
                    to={`${routePaths.projectOverview}`}
                  >
                    Project Overview
                  </NavDropdown.Item>
                )}

                {isAdmin && (
                  <NavDropdown.Item
                    className={
                      location.pathname === routePaths?.testResult ||
                      hoverState === 'TestResult'
                        ? 'user-active-link'
                        : ' '
                    }
                    onMouseEnter={() => onHoverView('TestResult')}
                    onMouseLeave={() => onHoverView('')}
                    as={Link}
                    to={`${routePaths.testResult}`}
                  >
                    {t('Header.testResult')}
                  </NavDropdown.Item>
                )}
                <NavDropdown.Divider />

                <Button
                  as="button"
                  onClick={() => handleSignout()}
                  className="login-btn btn dropdown-item"
                  onMouseEnter={() => onHoverView('logout')}
                  //onMouseLeave={() => onHoverView('')}
                >
                  {t('Header.signOut')}
                </Button>
              </NavDropdown>
            </div>
          ) : (
            <Nav className="signIn" data-testid="signIn">
              <Signin />
            </Nav>
          )}
        </div>

        <Navbar.Toggle className="toggle" aria-controls="basic-navbar-nav" />
      </Navbar>
    </Headroom>
  );
};

export default Header;
